/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// GPU Pods (QuickPods) - console redesign, Oct 2026.
//   /pods                 active pods (or empty state) + quick-start templates + recently stopped
//   /pods?view=history    every stopped / failed / terminated pod
// Templates live on /pods/templates (same tab bar).

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import GPUUnavailableModal from '../components/pods/GPUUnavailableModal';
import RelaunchPodModal, { isOOMFailure } from '../components/pods/RelaunchPodModal';
import { KYCRequiredModal } from '../components/kyc';
import { containerService, waitlistService, volumeService } from '../services/api';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { Container, LaunchTemplate } from '../types';
import { AxiosError } from 'axios';
import InsufficientBalanceModal from '../components/wallet/InsufficientBalanceModal';
import { useWallet } from '../hooks/useWallet';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { usePriceUnit } from '../hooks/usePriceUnit';
import { useKYCStatus } from '../hooks/useKYCStatus';
import { useContainerEvents } from '../hooks/useContainerEvents';
import { Page, PageHeader, Button, EmptyState, SectionHeader, Skeleton, Tag, StatusTag, statusTone } from '../components/console/ui';
import { ChipIc, PlusIc, SearchIc } from '../components/console/icons';
import PodsTabs, { PODS_HISTORY_PATH } from '../components/pods/PodsTabs';
import Dialog, { Callout } from '../components/pods/Dialog';
import {
  isInStock,
  isPopularTemplate,
  shortImage,
  useGpuCatalog,
  useSavedTemplates,
  useSellPrice,
  useTemplateCatalog,
} from '../components/pods/launch/catalog';

const STOPPED = ['stopped', 'failed', 'terminated'];
const isStopped = (c: Container) => STOPPED.includes(c.status?.toLowerCase() ?? '');

// Compact buttons that still meet 44px touch targets on small screens
const tbtn = 'ps-btn-sm max-[820px]:min-h-[44px]';

function isInferencePod(pod: Container): boolean {
  const image = (pod.image || '').toLowerCase();
  return (
    !!pod.endpoint ||
    image.includes('vllm') ||
    image.includes('tgi') ||
    image.includes('inference') ||
    image.includes('sdxl') ||
    (pod.name || '').toLowerCase().includes('endpoint')
  );
}

function formatGPU(pod: Container): string {
  if (!pod.gpu_type) return '-';
  let allocation = 'auto';
  if (pod.gpu_fraction && pod.gpu_fraction > 0) allocation = `${Math.round(pod.gpu_fraction * 100)}%`;
  else if (pod.gpu_count > 0) allocation = `${pod.gpu_count}×`;
  return `${pod.gpu_type} · ${allocation}`;
}

function hourlyRate(pod: Container): number {
  return (
    (pod.gpu_rate_per_hour || 0) +
    (pod.cpu_rate_per_hour || 0) +
    (pod.memory_rate_per_hour || 0) +
    (pod.storage_rate_per_hour || 0)
  );
}

function formatCost(pod: Container): string {
  const total = hourlyRate(pod);
  if (total <= 0) return '-';
  return `₹${Number.isInteger(total) ? total.toString() : total.toFixed(2)}/hr`;
}

function formatSub(pod: Container): string {
  const idShort = pod.id.slice(0, 7);
  if (pod.endpoint) return `${idShort} · ${pod.endpoint}`;
  if (pod.status?.toLowerCase() === 'failed' && pod.failure_reason) return `${idShort} · ${pod.failure_reason}`;
  if (isStopped(pod)) return `${idShort} · stopped`;
  return `${idShort} · started ${formatDistanceToNow(new Date(pod.created_at), { addSuffix: false })} ago`;
}

const PodStatus: React.FC<{ pod: Container }> = ({ pod }) =>
  pod.status?.toLowerCase() === 'running' && isInferencePod(pod) ? <Tag tone={statusTone('running')}>Serving</Tag> : <StatusTag status={pod.status} />;

const Pods: React.FC = () => {
  const queryClient = useQueryClient();
  const { perMinute } = usePriceUnit();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  const view = searchParams.get('view') === 'history' ? 'history' : 'pods';
  const { selectedProjectId: globalProjectId, autoSelectFirstProject } = useSelectedProject();

  // Use URL param if available, otherwise use global selection
  const selectedProject = projectFromUrl || globalProjectId;

  const { isKYCVerified } = useKYCStatus();
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [showGPUUnavailable, setShowGPUUnavailable] = useState(false);
  const [unavailableGPUType, setUnavailableGPUType] = useState<string>('');
  const [selectedPodForWaitlist, setSelectedPodForWaitlist] = useState<Container | null>(null);
  const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
  const [showInsufficientBalance, setShowInsufficientBalance] = useState(false);
  const [balanceErrorMessage, setBalanceErrorMessage] = useState('');
  const [podToDelete, setPodToDelete] = useState<Container | null>(null);
  const [podToStop, setPodToStop] = useState<Container | null>(null);
  const [podToRelaunch, setPodToRelaunch] = useState<Container | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Wallet data for balance check
  const { availableBalance, minimumRequired } = useWallet();

  // Auto-select first project if none selected and no URL param
  useEffect(() => {
    if (!projectFromUrl) autoSelectFirstProject();
  }, [projectFromUrl, autoSelectFirstProject]);

  const { connected: realtimeConnected } = useContainerEvents(selectedProject);

  const { data: containersData, isLoading } = useQuery({
    queryKey: ['containers', selectedProject],
    queryFn: () => containerService.list(selectedProject || undefined).then((res) => res.data),
    // Realtime socket pushes status changes; polling is only the fallback.
    refetchInterval: realtimeConnected ? 30000 : 10000,
  });

  // Handle both response formats: direct array or { data: [...] }
  const containers: Container[] = useMemo(
    () => (Array.isArray(containersData) ? containersData : containersData?.data || containersData?.containers || []),
    [containersData],
  );

  const activePods = useMemo(() => containers.filter((c) => !isStopped(c)), [containers]);
  const stoppedPods = useMemo(
    () =>
      containers
        .filter(isStopped)
        .sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime()),
    [containers],
  );
  const runningCount = useMemo(() => containers.filter((c) => c.status?.toLowerCase() === 'running').length, [containers]);

  const filterByQuery = (list: Container[]) => {
    const q = searchQuery.trim().toLowerCase();
    return q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list;
  };

  // Empty-state helpers: cheapest in-stock GPU, saved templates, volumes
  const { gpus, getAvailability } = useGpuCatalog();
  const { foldSellPrice } = useSellPrice();
  const cheapestInStock = useMemo(
    () =>
      [...gpus]
        .filter((g) => isInStock(getAvailability(g.gpu_type)))
        .sort((a, b) => a.price_per_minute - b.price_per_minute)[0],
    [gpus, getAvailability],
  );
  const { templates: savedTemplates } = useSavedTemplates(selectedProject || undefined);
  const { templates: catalogTemplates } = useTemplateCatalog(view === 'pods');
  const popularImages = useMemo(() => {
    const popular = catalogTemplates.filter(isPopularTemplate);
    return (popular.length ? popular : catalogTemplates).slice(0, 4);
  }, [catalogTemplates]);
  const { data: volumesData } = useQuery({
    queryKey: ['volumes', selectedProject],
    queryFn: () => volumeService.list(selectedProject!).then((res) => res.data),
    enabled: !!selectedProject && view === 'pods',
  });
  const volumeCount = (Array.isArray(volumesData) ? volumesData : volumesData?.data || volumesData?.storages || []).filter(
    (v: { status?: string }) => v.status === 'active',
  ).length;

  const startMutation = useMutation({
    mutationFn: ({ id, projectId, memoryGb }: { id: string; projectId: string; pod: Container; memoryGb?: number }) =>
      containerService.start(id, projectId, memoryGb ? { memory_gb: memoryGb } : undefined),
    onSuccess: (_res, variables) => {
      setPodToRelaunch(null);
      queryClient.invalidateQueries({ queryKey: ['containers'], refetchType: 'all' });
      toast.success(variables.pod.status?.toLowerCase() === 'failed' ? 'Pod relaunching...' : 'Pod starting...');
    },
    onError: (
      error: AxiosError<{ error?: string; code?: string; gpu_type?: string; can_join_waitlist?: boolean; message?: string }>,
      variables,
    ) => {
      // Insufficient balance (HTTP 402)
      if (error.response?.status === 402 || error.response?.data?.code === 'insufficient_balance') {
        setBalanceErrorMessage(error.response?.data?.message || 'Insufficient wallet balance to start this pod.');
        setShowInsufficientBalance(true);
        return;
      }
      // GPU unavailable → offer the waitlist
      if (error.response?.status === 409 && error.response?.data?.code === 'GPU_UNAVAILABLE' && error.response?.data?.can_join_waitlist) {
        setUnavailableGPUType(error.response.data.gpu_type || variables.pod.gpu_type || 'GPU');
        setSelectedPodForWaitlist(variables.pod);
        setShowGPUUnavailable(true);
        return;
      }
      toast.error(error.response?.data?.error || 'Failed to start pod');
    },
  });

  const stopMutation = useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string }) => containerService.stop(id, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers'], refetchType: 'all' });
      toast.success('Pod stopping...');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to stop pod');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string }) => containerService.delete(id, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers'], refetchType: 'all' });
      toast.success('Pod deleted');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to delete pod');
    },
  });

  const confirmDelete = () => {
    if (podToDelete) deleteMutation.mutate({ id: podToDelete.id, projectId: podToDelete.project_id });
    setPodToDelete(null);
  };

  const confirmStop = () => {
    if (podToStop) stopMutation.mutate({ id: podToStop.id, projectId: podToStop.project_id });
    setPodToStop(null);
  };

  // OOM kills get a confirm that suggests a memory bump; everything else starts directly.
  const handleStart = (pod: Container) => {
    if (pod.status?.toLowerCase() === 'failed' && isOOMFailure(pod.failure_reason)) {
      setPodToRelaunch(pod);
    } else {
      startMutation.mutate({ id: pod.id, projectId: pod.project_id, pod });
    }
  };

  const handleJoinWaitlist = async () => {
    if (!selectedPodForWaitlist) return;
    setIsJoiningWaitlist(true);
    try {
      const response = await waitlistService.join({
        gpu_type: unavailableGPUType || selectedPodForWaitlist.gpu_type || '',
        gpu_count: selectedPodForWaitlist.gpu_count || 1,
        project_id: selectedPodForWaitlist.project_id,
      });
      const queuePosition = response.data?.queue_position || 1;
      const totalInQueue = response.data?.total_in_queue || 1;
      toast.success(
        `Added to waitlist for ${unavailableGPUType}! Queue position: #${queuePosition} of ${totalInQueue}. Check your email for confirmation.`,
      );
      setShowGPUUnavailable(false);
      setSelectedPodForWaitlist(null);
    } catch (err) {
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setIsJoiningWaitlist(false);
    }
  };

  // Every launch entry point is KYC-gated
  const launch = (state?: { template?: LaunchTemplate; preselectedGpu?: string }) => {
    if (!isKYCVerified) setShowKYCModal(true);
    else navigate('/pods/create', state ? { state } : undefined);
  };

  const rowActions = (pod: Container) => {
    const s = pod.status?.toLowerCase();
    const starting = startMutation.isPending && startMutation.variables?.id === pod.id;
    const stopping = stopMutation.isPending && stopMutation.variables?.id === pod.id;
    return (
      <div className="flex justify-end gap-2">
        <Link to={`/pods/${pod.id}`} className={`ps-btn2 ${tbtn}`}>
          Open
        </Link>
        {s === 'running' ? (
          <button type="button" className={`ps-btn2 ${tbtn}`} onClick={() => setPodToStop(pod)} disabled={stopping}>
            {stopping ? 'Stopping…' : 'Stop'}
          </button>
        ) : s === 'stopped' || s === 'failed' ? (
          <button type="button" className={`ps-btn2 ${tbtn}`} onClick={() => handleStart(pod)} disabled={starting}>
            {starting ? 'Starting…' : s === 'failed' ? 'Relaunch' : 'Start'}
          </button>
        ) : null}
        <button type="button" className={`ps-btn-danger ${tbtn}`} onClick={() => setPodToDelete(pod)} aria-label={`Delete ${pod.name}`}>
          Delete
        </button>
      </div>
    );
  };

  const skeletonRows = (cols: number) =>
    Array.from({ length: 3 }).map((_, i) => (
      <tr key={i}>
        {Array.from({ length: cols }).map((__, j) => (
          <td key={j}>
            <Skeleton className="h-4 w-24" />
          </td>
        ))}
      </tr>
    ));

  const searchBox = (
    <label className="relative w-full sm:w-72">
      <span className="sr-only">Search pods</span>
      <SearchIc className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ps-subtle" />
      <input
        type="text"
        placeholder="Search pods…"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="ps-input pl-9"
      />
    </label>
  );

  const stoppedTable = (list: Container[]) => (
    <div className="ps-card overflow-x-auto">
      <table className="ps-tbl">
        <thead>
          <tr>
            <th>Pod</th>
            <th>GPU</th>
            <th>Resources</th>
            <th>Status</th>
            <th>Stopped</th>
            <th>Cost</th>
            <th>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? skeletonRows(7)
            : list.map((pod) => (
                <tr key={pod.id}>
                  <td>
                    <Link to={`/pods/${pod.id}`} className="font-mono text-ps-text hover:text-ps-accent">
                      {pod.name}
                    </Link>
                    {pod.status?.toLowerCase() === 'failed' && pod.failure_reason && (
                      <div className="text-xs text-ps-err max-w-[320px] truncate" title={pod.failure_reason}>
                        {pod.failure_reason}
                      </div>
                    )}
                  </td>
                  <td>{formatGPU(pod)}</td>
                  <td className="text-ps-muted">
                    {pod.cpu_cores} vCPU · {pod.memory_gb} GB
                  </td>
                  <td>
                    <PodStatus pod={pod} />
                  </td>
                  <td className="text-ps-muted">{format(new Date(pod.updated_at || pod.created_at), 'd MMM, HH:mm')}</td>
                  <td>
                    {pod.total_billed_inr !== undefined && pod.total_billed_inr !== null
                      ? `₹${pod.total_billed_inr.toFixed(2)}`
                      : formatCost(pod)}
                  </td>
                  <td>{rowActions(pod)}</td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );

  const filteredActive = filterByQuery(activePods);
  const filteredHistory = filterByQuery(stoppedPods);

  return (
    <Page>
      <PageHeader
        title="GPU Pods"
        subtitle="Containers on dedicated GPUs, billed per minute while running."
        actions={
          <Button onClick={() => launch()}>
            <PlusIc className="w-4 h-4" />
            Launch pod
          </Button>
        }
      />

      <PodsTabs
        active={view}
        runningCount={isLoading ? undefined : runningCount}
        templateCount={savedTemplates.length}
        historyCount={stoppedPods.length}
      />

      {view === 'history' ? (
        <section className="flex flex-col gap-3" aria-labelledby="hist">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <h2 id="hist" className="m-0 text-lg font-medium">
              Stopped, failed and terminated pods
            </h2>
            {searchBox}
          </div>
          {!isLoading && filteredHistory.length === 0 ? (
            <EmptyState
              icon={<ChipIc />}
              title={searchQuery.trim() ? 'No pods match your search' : 'No pod history yet'}
              body={searchQuery.trim() ? 'Try a different term or clear the search.' : 'Pods you stop show up here so you can relaunch them.'}
            />
          ) : (
            stoppedTable(filteredHistory)
          )}
        </section>
      ) : (
        <>
          {isLoading ? (
            <div className="ps-card overflow-x-auto">
              <table className="ps-tbl">
                <tbody>{skeletonRows(6)}</tbody>
              </table>
            </div>
          ) : activePods.length > 0 ? (
            <section className="flex flex-col gap-3" aria-labelledby="active">
              <div className="flex flex-wrap justify-between items-center gap-3">
                <h2 id="active" className="m-0 text-lg font-medium">
                  Active pods
                </h2>
                {activePods.length > 5 && searchBox}
              </div>
              <div className="ps-card overflow-x-auto">
                <table className="ps-tbl">
                  <thead>
                    <tr>
                      <th>Pod</th>
                      <th>GPU</th>
                      <th>Resources</th>
                      <th>Status</th>
                      <th>Cost</th>
                      <th>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActive.map((pod) => (
                      <tr key={pod.id}>
                        <td>
                          <Link to={`/pods/${pod.id}`} className="font-semibold text-ps-text hover:text-ps-accent">
                            {pod.name}
                          </Link>
                          <div className="font-mono text-xs text-ps-subtle max-w-[320px] truncate">{formatSub(pod)}</div>
                        </td>
                        <td>{formatGPU(pod)}</td>
                        <td className="text-ps-muted">
                          {pod.cpu_cores} vCPU · {pod.memory_gb} GB
                        </td>
                        <td>
                          <PodStatus pod={pod} />
                        </td>
                        <td className="font-mono">{formatCost(pod)}</td>
                        <td>{rowActions(pod)}</td>
                      </tr>
                    ))}
                    {filteredActive.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-ps-muted !py-8">
                          No pods match your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <EmptyState
              icon={<ChipIc />}
              title="No pods running"
              body={
                <>
                  Start from a GPU that’s in stock right now, or reuse a saved template.
                  {volumeCount > 0 &&
                    ` Your ${volumeCount} NFS volume${volumeCount > 1 ? 's' : ''} can be mounted at launch.`}
                </>
              }
              actions={
                <>
                  {cheapestInStock ? (
                    <Button onClick={() => launch({ preselectedGpu: cheapestInStock.gpu_type })}>
                      Launch {cheapestInStock.gpu_type} ·{' '}
                      {perMinute
                        ? `₹${foldSellPrice(cheapestInStock.price_per_minute).toFixed(2)}/min`
                        : `₹${(foldSellPrice(cheapestInStock.price_per_minute) * 60).toFixed(0)}/hr`}
                    </Button>
                  ) : (
                    <Button onClick={() => launch()}>Launch pod</Button>
                  )}
                  {gpus.length > 0 && (
                    <Button variant="secondary" onClick={() => launch()}>
                      Compare all {gpus.length} GPUs
                    </Button>
                  )}
                </>
              }
            />
          )}

          <section className="flex flex-col gap-3" aria-labelledby="tpl">
            <SectionHeader
              id="tpl"
              title="Quick start from a template"
              action={
                <Link to="/pods/templates" className="ps-link">
                  Manage templates →
                </Link>
              }
            />
            <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(min(100%,280px),1fr))]">
              {savedTemplates.slice(0, 2).map((t) => (
                <div key={t.id} className="ps-card p-[18px] flex flex-col gap-2.5">
                  <div className="flex justify-between items-center gap-2">
                    <span className="font-semibold truncate">{t.name}</span>
                    <Tag tone="info">{t.type === 'vm' ? 'VM' : 'Pod'}</Tag>
                  </div>
                  <div className="text-[13px] text-ps-muted">
                    Image <span className="font-mono">{shortImage(t.image_name)}</span>
                    {t.gpu_type && ` · ${t.gpu_type}`}
                    {t.cpu_cores > 0 && ` · ${t.cpu_cores} vCPU`}
                    {t.memory_gb > 0 && ` · ${t.memory_gb} GB RAM`}
                  </div>
                  <div className="text-xs text-ps-subtle">Saved {format(new Date(t.created_at), 'd MMM yyyy')}</div>
                  <div className="flex gap-2">
                    <button type="button" className={`ps-btn ${tbtn}`} onClick={() => launch({ template: t, preselectedGpu: t.gpu_type })}>
                      Launch
                    </button>
                    <Link to={`/pods/templates?edit=${t.id}`} className={`ps-btn2 ${tbtn}`}>
                      Edit
                    </Link>
                  </div>
                </div>
              ))}
              <div className="ps-card border-dashed p-[18px] flex flex-col gap-2.5 justify-center items-start">
                <span className="font-semibold">{savedTemplates.length ? 'Popular images' : 'No saved templates yet'}</span>
                {popularImages.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap">
                    {popularImages.map((t) => (
                      <Tag key={t.id} tone="off">
                        {t.name}
                      </Tag>
                    ))}
                  </div>
                )}
                <button type="button" className="ps-link text-sm min-h-[32px]" onClick={() => launch()}>
                  Browse the template gallery →
                </button>
              </div>
            </div>
          </section>

          {stoppedPods.length > 0 && (
            <section className="flex flex-col gap-3" aria-labelledby="rec">
              <SectionHeader
                id="rec"
                title="Recently stopped"
                action={
                  stoppedPods.length > 5 ? (
                    <Link to={PODS_HISTORY_PATH} className="ps-link">
                      View all {stoppedPods.length} →
                    </Link>
                  ) : undefined
                }
              />
              {stoppedTable(stoppedPods.slice(0, 5))}
            </section>
          )}
        </>
      )}

      <GPUUnavailableModal
        isOpen={showGPUUnavailable}
        onClose={() => {
          setShowGPUUnavailable(false);
          setSelectedPodForWaitlist(null);
        }}
        onJoinWaitlist={handleJoinWaitlist}
        gpuType={unavailableGPUType}
        containerName={selectedPodForWaitlist?.name}
        isJoining={isJoiningWaitlist}
      />

      <InsufficientBalanceModal
        isOpen={showInsufficientBalance}
        onClose={() => setShowInsufficientBalance(false)}
        message={balanceErrorMessage}
        requiredAmount={minimumRequired}
        currentBalance={availableBalance}
      />

      {/* OOM Relaunch Modal - suggests a memory bump before redeploying */}
      <RelaunchPodModal
        isOpen={!!podToRelaunch}
        pod={podToRelaunch}
        isPending={startMutation.isPending}
        onClose={() => setPodToRelaunch(null)}
        onRelaunch={(memoryGb) =>
          podToRelaunch &&
          startMutation.mutate({ id: podToRelaunch.id, projectId: podToRelaunch.project_id, pod: podToRelaunch, memoryGb })
        }
      />

      <Dialog
        open={!!podToDelete}
        onClose={() => setPodToDelete(null)}
        title="Delete pod"
        footer={
          <>
            <button type="button" className="ps-btn2" onClick={() => setPodToDelete(null)}>
              Cancel
            </button>
            <button type="button" className="ps-btn-danger" onClick={confirmDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete pod'}
            </button>
          </>
        }
      >
        {podToDelete && (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-ps-muted text-sm">
              Are you sure you want to delete <span className="font-semibold text-ps-text">“{podToDelete.name}”</span>? This action
              cannot be undone.
            </p>
            <Callout tone="warn" title="Billing notice">
              No pro-rated refund. You will be billed for the complete {podToDelete.billing_period || 'current'} billing cycle even
              if you delete now.
            </Callout>
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!podToStop}
        onClose={() => setPodToStop(null)}
        title="Stop pod"
        footer={
          <>
            <button type="button" className="ps-btn2" onClick={() => setPodToStop(null)}>
              Cancel
            </button>
            <button type="button" className="ps-btn" onClick={confirmStop} disabled={stopMutation.isPending}>
              {stopMutation.isPending ? 'Stopping…' : 'Stop pod'}
            </button>
          </>
        }
      >
        {podToStop && (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-ps-muted text-sm">
              Are you sure you want to stop <span className="font-semibold text-ps-text">“{podToStop.name}”</span>?
            </p>
            {(podToStop.gpu_count > 0 || (podToStop.gpu_fraction && podToStop.gpu_fraction > 0)) && (
              <Callout tone="warn" title="GPU will be released">
                Stopping this pod will release the {podToStop.gpu_type || 'GPU'} back to the pool. GPU availability is not
                guaranteed when you restart.
              </Callout>
            )}
            <Callout tone="warn" title="Data will be lost">
              Once the pod is stopped, any data not stored in NFS or other persistent storage will be permanently lost. Make sure
              important files are saved to persistent storage before stopping.
            </Callout>
            <Callout tone="off">Billing will be paused while the pod is stopped.</Callout>
          </div>
        )}
      </Dialog>

      <KYCRequiredModal isOpen={showKYCModal} onClose={() => setShowKYCModal(false)} />
    </Page>
  );
};

export default Pods;
